/**
 * Evaluation Component
 *
 * Drill-down interface for browsing stored match transcriptions and
 * running/viewing LLM-powered evaluations:
 * Worlds → Teams (cohorts) → Matches → Transcript / Evaluation viewer
 */

import { formatDateTime, formatDateTimeUTC, formatMinutesSeconds, formatTime } from '../../utils/format-date';
import { Card } from '../../components/card';
import { EmptyState } from '../../components/empty-state';
import { useState, useEffect, useCallback } from 'react';
import { Button } from '../../components/button';
import { useTranslation } from 'react-i18next';
import { CommonHeader } from '../shell/common-header';
import type {
  WorldEntry,
  TeamEntry,
  MatchEntry,
  PlayersChatFile,
  PlayerActionsFile,
} from '../../api/moderator/transcriptions/types';
import { useEvaluation, useRunEvaluation } from '../../api/moderator/evaluations/hooks';
import {
  useTranscriptionWorlds,
  useReadTranscriptionFile,
  useTranscriptionTeams,
  useTranscriptionMatches,
  useDeleteTranscriptionWorld,
} from '../../api/moderator/transcriptions/hooks';
import { useOntologyAction, useOntologyDecisions, useOntologyEvaluations, useOntologyStatus } from '../../api/moderator/ontology/hooks';
import { confirmDialog } from '../../components/confirm-dialog/confirm-dialog-store';
import { EvaluationDraftCard } from './evaluation-draft-card';
import evalStyles from './evaluation.module.css';
import utilities from '../../styles/utilities.module.css';
import type {
  OntologyActionSource,
  OntologySessionStatus,
} from '../../api/moderator/ontology/types';
import { ontologyEnumLabel } from './ontology-words';

/**
 * Class names these screens used to assemble from a value —
 * `ontology-status-${status.toLowerCase()}` and friends.
 *
 * A module generates its class names at build time, so a name put together at
 * runtime matches no rule: the badge keeps its shape and silently loses its
 * colour. Keying the enum to the member instead makes the mapping explicit,
 * and TypeScript now fails the build if a status is added without a style —
 * which the string version could never do.
 */
const ONTOLOGY_STATUS_TONE: Record<OntologySessionStatus, string> = {
  COMPLETE: evalStyles.ontologyStatusComplete,
  INCOMPLETE: evalStyles.ontologyStatusIncomplete,
  INGESTING: evalStyles.ontologyStatusIngesting,
};

const ONTOLOGY_SOURCE_TONE: Record<OntologyActionSource, string> = {
  HUMAN: evalStyles.ontologySourceHuman,
  BOT: evalStyles.ontologySourceBot,
  SYSTEM: evalStyles.ontologySourceSystem,
  MODERATOR: evalStyles.ontologySourceModerator,
};

const TIMELINE_BADGE_TONE: Record<'chat' | 'action', string> = {
  chat: evalStyles.evaluationTimelineBadgeChat,
  action: evalStyles.evaluationTimelineBadgeAction,
};

interface EvaluationProps {
  onBack: () => void;
}

type ViewState =
  | { level: 'worlds' }
  | { level: 'teams'; gameName: string; worldName: string }
  | { level: 'matches'; gameName: string; worldName: string; cohortId: string }
  | { level: 'transcript'; gameName: string; worldName: string; cohortId: string; match: MatchEntry }
  | { level: 'evaluation'; gameName: string; worldName: string; cohortId: string; match: MatchEntry }
  | { level: 'ontology'; gameName: string; worldName: string; cohortId: string; match: MatchEntry };

interface TimelineEntry {
  offsetSeconds: number;
  type: 'chat' | 'action';
  speaker: string;
  text: string;
  detail?: string;
}

export function Evaluation({ onBack }: EvaluationProps) {
  const { t } = useTranslation();
  const [view, setView] = useState<ViewState>({ level: 'worlds' });

  return (
    <div className={`portal-shell ${evalStyles.evaluationContainer}`}>
      <CommonHeader
        title={t('evaluation.title')}
        showBackButton
        onBackClick={onBack}
      />
      <main className={evalStyles.evaluationMain}>
        <div className={evalStyles.evaluationContent}>
          <Breadcrumb view={view} setView={setView} />

          {view.level === 'worlds' && (
            <WorldsList onSelect={(w) => setView({ level: 'teams', gameName: w.gameName, worldName: w.worldName })} />
          )}
          {view.level === 'teams' && (
            <TeamsList
              gameName={view.gameName}
              worldName={view.worldName}
              onSelect={(team) => setView({ level: 'matches', gameName: view.gameName, worldName: view.worldName, cohortId: team.cohortId })}
            />
          )}
          {view.level === 'matches' && (
            <MatchesList
              gameName={view.gameName}
              worldName={view.worldName}
              cohortId={view.cohortId}
              onSeeTranscript={(match) => setView({ level: 'transcript', gameName: view.gameName, worldName: view.worldName, cohortId: view.cohortId, match })}
              onSeeEvaluation={(match) => setView({ level: 'evaluation', gameName: view.gameName, worldName: view.worldName, cohortId: view.cohortId, match })}
              onSeeOntology={(match) => setView({ level: 'ontology', gameName: view.gameName, worldName: view.worldName, cohortId: view.cohortId, match })}
            />
          )}
          {view.level === 'transcript' && (
            <TranscriptView
              gameName={view.gameName}
              worldName={view.worldName}
              cohortId={view.cohortId}
              match={view.match}
            />
          )}
          {view.level === 'evaluation' && (
            <EvaluationView
              gameName={view.gameName}
              worldName={view.worldName}
              cohortId={view.cohortId}
              match={view.match}
            />
          )}
          {view.level === 'ontology' && (
            <DecisionExplorer match={view.match} cohortId={view.cohortId} />
          )}
        </div>
      </main>
    </div>
  );
}

/* ---------- Breadcrumb ---------- */

function Breadcrumb({ view, setView }: { view: ViewState; setView: (v: ViewState) => void }) {
  const { t } = useTranslation();

  const crumbs: { label: string; onClick?: () => void }[] = [
    { label: t('evaluation.worlds'), onClick: () => setView({ level: 'worlds' }) },
  ];

  if (view.level !== 'worlds') {
    crumbs.push({
      label: view.worldName,
      onClick: () => setView({ level: 'teams', gameName: view.gameName, worldName: view.worldName }),
    });
  }
  if (view.level === 'matches' || view.level === 'transcript' || view.level === 'evaluation' || view.level === 'ontology') {
    crumbs.push({
      label: view.cohortId,
      onClick: () => setView({ level: 'matches', gameName: view.gameName, worldName: view.worldName, cohortId: view.cohortId }),
    });
  }
  if (view.level === 'transcript') {
    crumbs.push({ label: formatDateTime(view.match.startedAt) });
  }
  if (view.level === 'evaluation') {
    crumbs.push({ label: t('evaluation.evaluationResults') });
  }
  if (view.level === 'ontology') {
    crumbs.push({ label: t('evaluation.ontology.title') });
  }

  return (
    <nav className={evalStyles.evaluationBreadcrumb}>
      {crumbs.map((crumb, i) => {
        const isLast = i === crumbs.length - 1;
        return (
          <span key={i}>
            {i > 0 && <span className={evalStyles.evaluationBreadcrumbSeparator}> / </span>}
            {isLast || !crumb.onClick ? (
              <span className={evalStyles.evaluationBreadcrumbCurrent}>{crumb.label}</span>
            ) : (
              <button type="button" className={evalStyles.evaluationBreadcrumbLink} onClick={crumb.onClick}>
                {crumb.label}
              </button>
            )}
          </span>
        );
      })}
    </nav>
  );
}


/* ---------- Worlds List ---------- */

function WorldsList({ onSelect }: { onSelect: (w: WorldEntry) => void }) {
  const { t } = useTranslation();
  const query = useTranscriptionWorlds();
  const deleteWorld = useDeleteTranscriptionWorld();
  const worlds: WorldEntry[] = query.data ?? [];
  const loading = query.isLoading;
  const [pageError, setPageError] = useState<string | null>(null);
  const queryError = query.isError ? t('evaluation.errorLoading') : null;
  const error = pageError ?? queryError;
  const [deletingKey, setDeletingKey] = useState<string | null>(null);

  const handleDelete = async (e: React.MouseEvent, w: WorldEntry) => {
    e.stopPropagation();
    if (!(await confirmDialog({ message: t('evaluation.deleteWorldConfirm'), tone: 'danger' }))) return;

    const key = `${w.gameName}/${w.worldName}`;
    setDeletingKey(key);
    setPageError(null);
    try {
      await deleteWorld.mutateAsync({ gameName: w.gameName, worldName: w.worldName });
    } catch {
      setPageError(t('evaluation.deleteWorldError'));
    } finally {
      setDeletingKey(null);
    }
  };

  if (loading) return <div className={evalStyles.evaluationLoading}>{t('evaluation.loadingWorlds')}</div>;
  if (error) return <div className={evalStyles.evaluationError}>{error}</div>;
  if (worlds.length === 0) return <EmptyState surface>{t('evaluation.noWorlds')}</EmptyState>;

  return (
    <>
      <Card framed className={evalStyles.evaluationSectionHeader}>
        <h2 className={evalStyles.evaluationSectionTitle}>{t('evaluation.worlds')}</h2>
        <p className={evalStyles.evaluationSectionSubtitle}>{t('evaluation.selectWorld')}</p>
      </Card>
      <div className={evalStyles.evaluationGrid}>
        {[...worlds].sort((a, b) => {
          if (a.latestMatchDate && b.latestMatchDate) return b.latestMatchDate.localeCompare(a.latestMatchDate);
          if (a.latestMatchDate) return -1;
          if (b.latestMatchDate) return 1;
          return 0;
        }).map((w) => {
          const key = `${w.gameName}/${w.worldName}`;
          const isDeleting = deletingKey === key;
          return (
            <div key={key} className={evalStyles.evaluationWorldCell}>
              <Card framed onClick={() => onSelect(w)} aria-label={w.worldName}>
              <div className={evalStyles.evaluationCardIcon}>🌍</div>
              <h3 className={evalStyles.evaluationCardTitle}>{w.worldName}</h3>
              <p className={evalStyles.evaluationCardMeta}>{w.gameName}</p>
              {w.latestMatchDate && (
                <p className={evalStyles.evaluationCardDetail}>
                  {t('evaluation.latestMatch')}{' '}
                  {formatDateTimeUTC(w.latestMatchDate)}
                </p>
              )}
              </Card>
              {/* Sibling, not child: a <button> inside a <button> is invalid
                  markup and unreachable by keyboard. */}
              <button
                type="button"
                className={evalStyles.evaluationCardDelete}
                disabled={isDeleting}
                onClick={(e) => handleDelete(e, w)}
                title={t('evaluation.deleteWorld')}
              >
                {isDeleting ? t('evaluation.deletingWorld') : '🗑'}
              </button>
            </div>
          );
        })}
      </div>
    </>
  );
}

/* ---------- Teams List ---------- */

function TeamsList({ gameName, worldName, onSelect }: { gameName: string; worldName: string; onSelect: (t: TeamEntry) => void }) {
  const { t } = useTranslation();
  const query = useTranscriptionTeams(gameName, worldName);
  const teams: TeamEntry[] = query.data ?? [];
  const loading = query.isLoading;
  const error = query.isError ? t('evaluation.errorLoading') : null;

  if (loading) return <div className={evalStyles.evaluationLoading}>{t('evaluation.loadingTeams')}</div>;
  if (error) return <div className={evalStyles.evaluationError}>{error}</div>;
  if (teams.length === 0) return <EmptyState surface>{t('evaluation.noTeams')}</EmptyState>;

  return (
    <>
      <Card framed className={evalStyles.evaluationSectionHeader}>
        <h2 className={evalStyles.evaluationSectionTitle}>{t('evaluation.teams')}</h2>
        <p className={evalStyles.evaluationSectionSubtitle}>{t('evaluation.selectTeam')}</p>
      </Card>
      <div className={evalStyles.evaluationGrid}>
        {teams.map((team) => (
          <Card
            key={team.cohortId}
            framed
            onClick={() => onSelect(team)}
            aria-label={team.cohortId}
          >
            <div className={evalStyles.evaluationCardIcon}>👥</div>
            <h3 className={evalStyles.evaluationCardTitle}>{team.cohortId}</h3>
            {team.playerNames.length > 0 && (
              <p className={evalStyles.evaluationCardDetail}>{team.playerNames.join(', ')}</p>
            )}
          </Card>
        ))}
      </div>
    </>
  );
}

/* ---------- Matches List ---------- */

function MatchesList({
  gameName,
  worldName,
  cohortId,
  onSeeTranscript,
  onSeeEvaluation,
  onSeeOntology,
}: {
  gameName: string;
  worldName: string;
  cohortId: string;
  onSeeTranscript: (m: MatchEntry) => void;
  onSeeEvaluation: (m: MatchEntry) => void;
  onSeeOntology: (m: MatchEntry) => void;
}) {
  const { t } = useTranslation();
  const query = useTranscriptionMatches(gameName, worldName, cohortId);
  const runEval = useRunEvaluation();
  const matches: MatchEntry[] = query.data ?? [];
  const loading = query.isLoading;
  const [pageError, setPageError] = useState<string | null>(null);
  const queryError = query.isError ? t('evaluation.errorLoading') : null;
  const error = pageError ?? queryError;
  const [runningFolder, setRunningFolder] = useState<string | null>(null);

  const loadMatches = () => { void query.refetch(); };

  const handleRunEvaluation = async (match: MatchEntry) => {
    setRunningFolder(match.matchFolder);
    setPageError(null);
    try {
      await runEval.mutateAsync({ gameName, worldName, cohortId, matchFolder: match.matchFolder });
      loadMatches();
      onSeeEvaluation(match);
    } catch (err) {
      console.error('[Evaluation] Failed to run evaluation:', err);
      setPageError(t('evaluation.evaluationFailed'));
    } finally {
      setRunningFolder(null);
    }
  };

  if (loading) return <div className={evalStyles.evaluationLoading}>{t('evaluation.loadingMatches')}</div>;
  if (error) return <div className={evalStyles.evaluationError}>{error}</div>;
  if (matches.length === 0) return <EmptyState surface>{t('evaluation.noMatches')}</EmptyState>;

  // Sort chronologically (oldest first) to determine evaluation order
  const chronological = [...matches].sort(
    (a, b) => a.startedAt.localeCompare(b.startedAt),
  );

  // Find the first unevaluated match folder
  const firstUnevaluatedFolder = chronological.find(
    (m) => !m.files.some((f) => f.type === 'evaluation'),
  )?.matchFolder ?? null;

  return (
    <>
      <Card framed className={evalStyles.evaluationSectionHeader}>
        <h2 className={evalStyles.evaluationSectionTitle}>{t('evaluation.matches')}</h2>
        <p className={evalStyles.evaluationSectionSubtitle}>{cohortId}</p>
      </Card>
      <div className={evalStyles.evaluationMatchesList}>
        {matches.map((match) => {
          const hasEvaluation = match.files.some((f) => f.type === 'evaluation');
          // The ontology artifact is the feature flag: the recorder is off in most
          // environments. The button still renders when it is missing, disabled and
          // explaining why — a match that simply has no decision record is normal,
          // and a control that vanishes reads as a bug rather than as an absence.
          const hasOntology = match.files.some((f) => f.type === 'ontology');
          const canRunEvaluation = !hasEvaluation && match.matchFolder === firstUnevaluatedFolder;
          const isRunning = runningFolder === match.matchFolder;

          return (
            <div key={match.matchFolder} className={evalStyles.evaluationMatchRow}>
              <div className={evalStyles.evaluationMatchInfo}>
                <span className={evalStyles.evaluationMatchTimestamp}>{formatDateTime(match.startedAt)}</span>
                <span className={evalStyles.evaluationMatchFiles}>
                  {match.files.filter((f) => f.type !== 'evaluation').map((f) => f.type).join(', ')}
                  {hasEvaluation && (
                    <span className={evalStyles.evaluationMatchBadgeEvaluated}>
                      {t('evaluation.evaluated')}
                    </span>
                  )}
                </span>
              </div>
              <div className={evalStyles.evaluationMatchActions}>
                <Button
                  type="button"
                  variant={hasOntology ? 'primary' : 'ghost'}
                  onClick={() => onSeeOntology(match)}
                  disabled={!hasOntology}
                  title={hasOntology ? undefined : t('evaluation.ontology.unavailableReason')}
                  aria-describedby={
                    hasOntology ? undefined : `ontology-unavailable-${match.matchFolder}`
                  }
                >
                  {t('evaluation.ontology.title')}
                </Button>
                {!hasOntology && (
                  // A title attribute is invisible to screen readers on a disabled
                  // control, so the reason is also exposed as associated text.
                  <span
                    id={`ontology-unavailable-${match.matchFolder}`}
                    className={utilities.visuallyHidden}
                  >
                    {t('evaluation.ontology.unavailableReason')}
                  </span>
                )}
                <Button type="button" variant="ghost" onClick={() => onSeeTranscript(match)}
                >
                  {t('evaluation.seeTranscript')}
                </Button>
                {hasEvaluation ? (
                  <Button type="button" variant="gold" onClick={() => onSeeEvaluation(match)}
                  >
                    {t('evaluation.seeEvaluations')}
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="gold"
                    disabled={!canRunEvaluation || isRunning}
                    onClick={() => canRunEvaluation && handleRunEvaluation(match)}
                  >
                    {isRunning ? t('evaluation.runningEvaluations') : t('evaluation.runEvaluations')}
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

/* ---------- Ontology Decision Explorer (moderator-only evidence) ---------- */

function DecisionExplorer({ match, cohortId }: { match: MatchEntry; cohortId: string }) {
  const { t } = useTranslation();
  const [selectedActionId, setSelectedActionId] = useState<string | null>(null);
  const status = useOntologyStatus(match.matchId);
  const decisions = useOntologyDecisions(match.matchId, cohortId);
  const packet = useOntologyAction(match.matchId, selectedActionId);
  const evaluations = useOntologyEvaluations(match.matchId, cohortId);

  if (status.isLoading || decisions.isLoading) {
    return <div className={evalStyles.evaluationLoading}>{t('evaluation.ontology.loading')}</div>;
  }
  // A disabled query (empty matchId on legacy match folders) reports neither
  // loading nor error and never resolves data, so the data itself is the guard.
  if (status.isError || !status.data || decisions.isError) {
    return <EmptyState surface>{t('evaluation.ontology.unavailable')}</EmptyState>;
  }

  const audit = status.data;
  const records = decisions.data ?? [];
  return (
    <section className={evalStyles.ontologyExplorer}>
      <Card framed className={`${evalStyles.evaluationSectionHeader} ${evalStyles.ontologyExplorerHeader}`}>
        <div>
          <h2 className={evalStyles.evaluationSectionTitle}>{t('evaluation.ontology.title')}</h2>
          <p className={evalStyles.evaluationSectionSubtitle}>{t('evaluation.ontology.subtitle')}</p>
        </div>
        <div className={`${evalStyles.ontologyStatus} ${ONTOLOGY_STATUS_TONE[audit.status]}`}>
          <strong>{ontologyEnumLabel(t, 'sessionStatus', audit.status)}</strong>
          <span>
            {t('evaluation.ontology.auditSummary', {
              actions: audit.actionCount,
              gaps: audit.gapCount,
              transcript: ontologyEnumLabel(t, 'transcriptStatus', audit.transcriptStatus),
            })}
          </span>
          {/* Opening a decision packet makes the server import late-arriving
              transcripts, which can change this badge. Say that it is happening
              instead of letting the status appear to change on its own. */}
          {status.isFetching && !status.isLoading && (
            <span className={evalStyles.ontologyStatusRefreshing}>
              {t('evaluation.ontology.refreshingAudit')}
            </span>
          )}
        </div>
      </Card>

      {audit.status !== 'COMPLETE' && (
        <div className={evalStyles.ontologyWarning}>{t('evaluation.ontology.incompleteWarning')}</div>
      )}

      {/* Collapsed by default: with a long action list the review would otherwise
          push the record itself off-screen. The count in the summary means you can
          tell there is something to read without expanding. */}
      <details className={evalStyles.ontologyEvaluationReview}>
        <summary>
          <span className={evalStyles.ontologyEvaluationReviewTitle}>
            {t('evaluation.ontology.evaluationReview')}
          </span>
          <span className={evalStyles.ontologyEvaluationReviewCount}>
            {t('evaluation.ontology.draftCount', { count: evaluations.data?.length ?? 0 })}
          </span>
        </summary>
        <div className={evalStyles.ontologyEvaluationReviewBody}>
        <p className={evalStyles.ontologyMuted}>{t('evaluation.ontology.evaluationReviewSubtitle')}</p>
        {evaluations.isLoading ? (
          <div className={evalStyles.evaluationLoading}>{t('evaluation.ontology.loadingDrafts')}</div>
        ) : evaluations.isError || !evaluations.data?.length ? (
          <EmptyState surface>{t('evaluation.ontology.noDrafts')}</EmptyState>
        ) : (
          <div className={evalStyles.ontologyEvaluationList}>
            {evaluations.data.map((evaluation) => (
              <EvaluationDraftCard
                key={evaluation.id}
                evaluation={evaluation}
                matchId={match.matchId}
                onSelectAction={setSelectedActionId}
              />
            ))}
          </div>
        )}
        </div>
      </details>

      <div className={evalStyles.ontologyLayout}>
        <div className={evalStyles.ontologyActionList}>
          {records.length === 0 ? (
            <EmptyState surface>{t('evaluation.ontology.noActions')}</EmptyState>
          ) : records.map((record) => (
            <button
              type="button"
              key={record.id}
              className={`${evalStyles.ontologyActionRow} ${selectedActionId === record.id ? evalStyles.isSelected : ''}`}
              onClick={() => setSelectedActionId(record.id)}
            >
              <span className={evalStyles.ontologyActionSequence}>
                {t('evaluation.ontology.sequence', { sequence: record.sequence })}
              </span>
              <span className={evalStyles.ontologyActionMain}>
                <strong>{humanizeOntologyType(record.actionTypeId)}</strong>
                <small>
                  {t('evaluation.ontology.actorMeta', {
                    actor: record.actorDisplayName || ontologyEnumLabel(t, 'source', record.source),
                    timestamp: formatDateTime(record.occurredAt),
                  })}
                </small>
              </span>
              <span className={`${evalStyles.ontologySource} ${ONTOLOGY_SOURCE_TONE[record.source]}`}>
                {ontologyEnumLabel(t, 'source', record.source)}
              </span>
            </button>
          ))}
        </div>

        <div className={evalStyles.ontologyDetail}>
          {!selectedActionId ? (
            <EmptyState surface>{t('evaluation.ontology.selectAction')}</EmptyState>
          ) : packet.isLoading ? (
            <div className={evalStyles.evaluationLoading}>{t('evaluation.ontology.loadingEvidence')}</div>
          ) : packet.isError || !packet.data ? (
            <div className={evalStyles.evaluationError}>{t('evaluation.ontology.evidenceError')}</div>
          ) : (
            <>
              <h3>{humanizeOntologyType(packet.data.action.actionTypeId)}</h3>
              <p className={evalStyles.ontologyDetailMeta}>
                {t('evaluation.ontology.actionMeta', {
                  id: packet.data.action.id,
                  phase: packet.data.action.phase || t('evaluation.ontology.unknownPhase'),
                })}
              </p>

              <h4>{t('evaluation.ontology.parameters')}</h4>
              <pre>{JSON.stringify(packet.data.action.parameters, null, 2)}</pre>

              <h4>{t('evaluation.ontology.objects')}</h4>
              <div className={evalStyles.ontologyObjectList}>
                {packet.data.action.objects.map((role) => (
                  <div key={`${role.role}:${role.object.id}`} className={evalStyles.ontologyObjectRow}>
                    <span>{ontologyEnumLabel(t, 'objectRole', role.role)}</span>
                    <strong>{role.object.displayName || role.object.objectKey}</strong>
                    <small>
                      {t('evaluation.ontology.objectMeta', {
                        objectType: role.object.objectTypeId,
                        version: role.resultingVersion?.version ?? t('evaluation.ontology.notAvailable'),
                      })}
                    </small>
                    {role.priorVersion && role.resultingVersion && role.priorVersion.stateHash !== role.resultingVersion.stateHash && (
                      <details>
                        <summary>{t('evaluation.ontology.beforeAndAfter')}</summary>
                        <div className={evalStyles.ontologyVersionComparison}>
                          <div>
                            <b>{t('evaluation.ontology.beforeVersion', { version: role.priorVersion.version })}</b>
                            <pre>{JSON.stringify(role.priorVersion.properties, null, 2)}</pre>
                          </div>
                          <div>
                            <b>{t('evaluation.ontology.afterVersion', { version: role.resultingVersion.version })}</b>
                            <pre>{JSON.stringify(role.resultingVersion.properties, null, 2)}</pre>
                          </div>
                        </div>
                      </details>
                    )}
                    {role.nextObservedVersion && (
                      <small>
                        {t('evaluation.ontology.nextObservedChange', {
                          version: role.nextObservedVersion.version,
                          sequence: role.nextObservedVersion.sequence,
                        })}
                      </small>
                    )}
                  </div>
                ))}
              </div>

              <h4>{t('evaluation.ontology.laterObservations')}</h4>
              {packet.data.laterObservations.outcomes.length === 0 ? (
                <p className={evalStyles.ontologyMuted}>{t('evaluation.ontology.noOutcomes')}</p>
              ) : (
                <div className={evalStyles.ontologyLaterObservations}>
                  {packet.data.laterObservations.outcomes.map((outcome) => (
                    <div key={outcome.id}>
                      <strong>
                        {t('evaluation.ontology.simulationTick', {
                          tick: outcome.gameTick ?? t('evaluation.ontology.notAvailable'),
                        })}
                      </strong>
                      <small>
                        {t('evaluation.ontology.outcomeMeta', { id: outcome.id, sequence: outcome.sequence })}
                      </small>
                    </div>
                  ))}
                </div>
              )}

              <h4>{t('evaluation.ontology.discussion')}</h4>
              {packet.data.discussionWindow.utterances.length === 0 ? (
                <p className={evalStyles.ontologyMuted}>{t('evaluation.ontology.noUtterances')}</p>
              ) : (
                <div className={evalStyles.ontologyDiscussion}>
                  {packet.data.discussionWindow.utterances.map((utterance) => (
                    <p key={utterance.id}>
                      <strong>{utterance.speakerName}</strong>{' '}
                      <time>{formatTime(utterance.occurredAt)}</time>
                      <br />{utterance.text}
                    </p>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

    </section>
  );
}

function humanizeOntologyType(typeId: string) {
  return typeId.split('_').map((word) => word ? word[0].toUpperCase() + word.slice(1) : word).join(' ');
}

/* ---------- Transcript View ---------- */

function TranscriptView({
  gameName,
  worldName,
  cohortId,
  match,
}: {
  gameName: string;
  worldName: string;
  cohortId: string;
  match: MatchEntry;
}) {
  const { t } = useTranslation();
  const [timeline, setTimeline] = useState<TimelineEntry[]>([]);
  const [meta, setMeta] = useState<{ teamName?: string; duration?: number; players?: string[] }>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const readFile = useReadTranscriptionFile();
  const loadTranscript = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const entries: TimelineEntry[] = [];
      let teamName: string | undefined;
      let duration: number | undefined;
      const playerNames = new Set<string>();

      for (const file of match.files) {
        if (file.type === 'evaluation') continue;

        const data = await readFile(
          gameName,
          worldName,
          cohortId,
          match.matchFolder,
          file.filename,
        );

        if (file.type === 'players-chat') {
          const chatData = data as PlayersChatFile;
          duration = duration ?? chatData.duration_seconds;
          const participants = chatData.participants ?? [];
          for (const p of participants) {
            playerNames.add(p.name);
          }
          for (const entry of chatData.transcripts ?? []) {
            // Support both formats:
            // - Rich format (current): offset_seconds + speaker_name/speaker_identity
            // - Compact format (legacy): t + p (participant index)
            const offsetSeconds = entry.offset_seconds ?? entry.t ?? 0;
            // A speaker the file does not name is said so in the moderator's language.
            const speaker =
              entry.speaker_name ||
              participants[entry.p ?? -1]?.name ||
              entry.speaker_identity ||
              (entry.p !== undefined
                ? t('evaluation.transcript.participantNumber', { number: entry.p })
                : t('players.unknownSpeaker'));
            entries.push({
              offsetSeconds,
              type: 'chat',
              speaker,
              text: entry.text,
            });
          }
        }

        if (file.type === 'action-log') {
          const actionsData = data as PlayerActionsFile;
          teamName = actionsData.team?.name;
          duration = duration ?? actionsData.duration_seconds;
          const players = actionsData.players ?? [];
          const teamDisplayName = actionsData.team?.name?.trim();
          for (const action of actionsData.actions ?? []) {
            const speaker =
              typeof action.p === 'number'
                ? (players[action.p]?.display_name ?? `Player ${action.p}`)
                : teamDisplayName
                  ? t('evaluation.teamActionSpeaker', { name: teamDisplayName })
                  : t('evaluation.team');
            entries.push({
              offsetSeconds: action.t,
              type: 'action',
              speaker,
              text: action.action_type.replace(/_/g, ' '),
              detail: formatActionDetails(action.details),
            });
          }
        }
      }

      entries.sort((a, b) => a.offsetSeconds - b.offsetSeconds);
      setTimeline(entries);
      setMeta({ teamName, duration, players: [...playerNames] });
    } catch {
      setError(t('evaluation.errorLoading'));
    } finally {
      setLoading(false);
    }
  }, [gameName, worldName, cohortId, match, t, readFile]);

  useEffect(() => { loadTranscript(); }, [loadTranscript]);

  if (loading) return <div className={evalStyles.evaluationLoading}>{t('evaluation.loadingTranscript')}</div>;
  if (error) return <div className={evalStyles.evaluationError}>{error}</div>;

  return (
    <div className={evalStyles.evaluationTranscript}>
      <Card framed className={evalStyles.evaluationSectionHeader}>
        <h2 className={evalStyles.evaluationSectionTitle}>{t('evaluation.transcript.title')}</h2>
        <p className={evalStyles.evaluationSectionSubtitle}>{formatDateTime(match.startedAt)}</p>
      </Card>

      <div className={evalStyles.evaluationTranscriptMeta}>
        {meta.teamName && (
          <div className={evalStyles.evaluationTranscriptMetaItem}>
            <span className={evalStyles.evaluationTranscriptMetaLabel}>{t('evaluation.transcript.teamName')}</span>
            <span className={evalStyles.evaluationTranscriptMetaValue}>{meta.teamName}</span>
          </div>
        )}
        {meta.duration != null && (
          <div className={evalStyles.evaluationTranscriptMetaItem}>
            <span className={evalStyles.evaluationTranscriptMetaLabel}>{t('evaluation.transcript.duration')}</span>
            <span className={evalStyles.evaluationTranscriptMetaValue}>{formatDuration(meta.duration)}</span>
          </div>
        )}
        {meta.players && meta.players.length > 0 && (
          <div className={evalStyles.evaluationTranscriptMetaItem}>
            <span className={evalStyles.evaluationTranscriptMetaLabel}>{t('evaluation.transcript.players')}</span>
            <span className={evalStyles.evaluationTranscriptMetaValue}>{meta.players.join(', ')}</span>
          </div>
        )}
      </div>

      {timeline.length === 0 ? (
        <EmptyState surface>{t('evaluation.transcript.noEntries')}</EmptyState>
      ) : (
        <div className={evalStyles.evaluationTimeline}>
          {timeline.map((entry, i) => (
            <div key={i} className={evalStyles.evaluationTimelineEntry}>
              <span className={evalStyles.evaluationTimelineOffset}>{formatMinutesSeconds(entry.offsetSeconds)}</span>
              <div className={evalStyles.evaluationTimelineBody}>
                <div className={evalStyles.evaluationTimelineHeader}>
                  <span className={`${evalStyles.evaluationTimelineBadge} ${TIMELINE_BADGE_TONE[entry.type]}`}>
                    {entry.type === 'chat' ? t('evaluation.transcript.chat') : t('evaluation.transcript.action')}
                  </span>
                  {entry.speaker && <span className={evalStyles.evaluationTimelineSpeaker}>{entry.speaker}</span>}
                  {entry.type === 'action' && <span className={evalStyles.evaluationTimelineActionText}>{entry.text}</span>}
                </div>
                {entry.type === 'chat' && <div className={evalStyles.evaluationTimelineText}>{entry.text}</div>}
                {entry.detail && <div className={evalStyles.evaluationTimelineActionDetail}>{entry.detail}</div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- Evaluation Results View ---------- */

function EvaluationView({
  gameName,
  worldName,
  cohortId,
  match,
}: {
  gameName: string;
  worldName: string;
  cohortId: string;
  match: MatchEntry;
}) {
  const { t } = useTranslation();
  const query = useEvaluation({ gameName, worldName, cohortId, matchFolder: match.matchFolder });
  const result = query.data ?? null;
  const loading = query.isLoading;
  const error = query.isError ? t('evaluation.errorLoading') : null;

  if (loading) return <div className={evalStyles.evaluationLoading}>{t('evaluation.loadingEvaluations')}</div>;
  if (error) return <div className={evalStyles.evaluationError}>{error}</div>;
  if (!result) return <EmptyState surface>{t('evaluation.noEvaluations')}</EmptyState>;

  return (
    <div className={evalStyles.evaluationResults}>
      <Card framed className={evalStyles.evaluationSectionHeader}>
        <h2 className={evalStyles.evaluationSectionTitle}>{t('evaluation.evaluationResults')}</h2>
        <p className={evalStyles.evaluationSectionSubtitle}>
          {t('evaluation.sessionLabel', { number: result.session_number })} — {formatDateTime(match.startedAt)}
        </p>
      </Card>

      <div className={evalStyles.evaluationResultsMeta}>
        <div className={evalStyles.evaluationTranscriptMetaItem}>
          <span className={evalStyles.evaluationTranscriptMetaLabel}>{t('evaluation.model')}</span>
          <span className={evalStyles.evaluationTranscriptMetaValue}>{result.model}</span>
        </div>
        <div className={evalStyles.evaluationTranscriptMetaItem}>
          <span className={evalStyles.evaluationTranscriptMetaLabel}>{t('evaluation.evaluatedAt')}</span>
          <span className={evalStyles.evaluationTranscriptMetaValue}>{formatDateTime(result.evaluated_at)}</span>
        </div>
        <div className={evalStyles.evaluationTranscriptMetaItem}>
          <span className={evalStyles.evaluationTranscriptMetaLabel}>{t('evaluation.participants')}</span>
          <span className={evalStyles.evaluationTranscriptMetaValue}>{result.evaluations.length}</span>
        </div>
      </div>

      <div className={evalStyles.evaluationResultsCards}>
        {result.evaluations.map((evalItem) => (
          <div key={evalItem.participant_identity} className={evalStyles.evaluationResultCard}>
            <div className={evalStyles.evaluationResultCardHeader}>
              <span className={evalStyles.evaluationResultCardAvatar}>
                {evalItem.participant_name.charAt(0).toUpperCase()}
              </span>
              <h3 className={evalStyles.evaluationResultCardName}>{evalItem.participant_name}</h3>
            </div>
            <div className={evalStyles.evaluationResultCardBody}>
              {evalItem.evaluation.split('\n\n').map((paragraph, i) => (
                <p key={i}>{paragraph}</p>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- Helpers ---------- */


function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

function formatActionDetails(details: Record<string, unknown>): string {
  return Object.entries(details)
    .map(([k, v]) => `${k}: ${v}`)
    .join(', ');
}
